'use client';

import { useState } from 'react';
import Link from 'next/link';

import { usePathname } from 'next/navigation';
import { getCdnUrl } from '@/utils/cdn';
import { motion, AnimatePresence } from 'framer-motion';
import { CgClose } from 'react-icons/cg';
import { FiChevronDown } from 'react-icons/fi';

interface NavbarProps {
  isSticky?: boolean;
}

export default function Navbar({ isSticky = true }: NavbarProps) {
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileEmpreendimentosOpen, setIsMobileEmpreendimentosOpen] = useState(false);

  const navClasses = isSticky
    ? "fixed top-0 left-0 right-0 z-50 bg-[#F0F0F0]"
    : "relative z-50 bg-[#F0F0F0]";

  const otherNavLinks = [
    { href: '/sobre', label: 'Sobre nós' },
    { href: '/contato', label: 'Contato' },
    { href: '/cadastro-oportunidade', label: 'Cadastro' },
  ];

  const isEmpreendimentosActive = pathname === '/park-europa' || pathname === '/park-alterosa';
  const whatsappProject = pathname === '/park-alterosa' ? 'Park Alterosa' : 'Park Europa';
  const whatsappHref = `https://wa.me/5532998809292?text=${encodeURIComponent(`Oi! Vim da página do Empreendimento ${whatsappProject}. Gostaria de mais informações. Pode me ajudar?`)}`;

  return (
    <>
      <nav className={navClasses}>
        <div className="mx-auto px-5 sm:px-8 md:px-10 lg:px-16 xl:px-20 2xl:max-w-[1500px]">
          <div className="flex justify-between items-center h-[82px]">

            {/* --- LEFT SLOT --- */}
            <div className="flex justify-start shrink-0">
              <Link href="/">
                <img
                  src={getCdnUrl("/images/common/logo-forbli.webp")}
                  alt="Forbli Logo"
                  width={220}
                  height={83}
                  className="h-[40px] sm:h-[44px] w-auto"
                />
              </Link>
            </div>

            {/* --- CENTER SLOT (Desktop Links) --- */}
            <div className="hidden md:flex items-center space-x-6 lg:space-x-8 xl:space-x-10">
              <Link
                href="/"
                className={`text-base lg:text-lg font-bold transition-colors ${pathname === '/'
                  ? 'text-forbli-green border-b-2 border-forbli-green hover:text-forbli-green pb-1'
                  : 'text-gray-900 hover:text-forbli-green'
                  }`}
              >
                Início
              </Link>

              {/* Empreendimentos Dropdown (Non-clickable link trigger) */}
              <div
                className="relative"
                onMouseEnter={() => setIsDropdownOpen(true)}
                onMouseLeave={() => setIsDropdownOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen((prev) => !prev)}
                  className={`text-base lg:text-lg font-bold transition-colors flex items-center gap-1 focus:outline-none cursor-pointer ${
                    isEmpreendimentosActive
                      ? 'text-forbli-green border-b-2 border-forbli-green hover:text-forbli-green pb-1'
                      : 'text-gray-900 hover:text-forbli-green'
                  }`}
                >
                  <span>Empreendimentos</span>
                  <FiChevronDown className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180 text-forbli-green' : ''}`} />
                </button>

                <AnimatePresence>
                  {isDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.96 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2.5 w-52 rounded-2xl bg-white p-2 shadow-2xl border border-gray-100/80 z-50"
                    >
                      <Link
                        href="/park-europa"
                        className={`block px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                          pathname === '/park-europa'
                            ? 'bg-[#F5F7F5] text-forbli-green font-bold'
                            : 'text-gray-800 hover:bg-[#F5F7F5] hover:text-forbli-green'
                        }`}
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        Park Europa
                      </Link>
                      <Link
                        href="/park-alterosa"
                        className={`block px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                          pathname === '/park-alterosa'
                            ? 'bg-[#F5F7F5] text-forbli-green font-bold'
                            : 'text-gray-800 hover:bg-[#F5F7F5] hover:text-forbli-green'
                        }`}
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        Park Alterosa
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {otherNavLinks.map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`text-base lg:text-lg font-bold transition-colors ${pathname === link.href
                    ? 'text-forbli-green border-b-2 border-forbli-green hover:text-forbli-green pb-1'
                    : 'text-gray-900 hover:text-forbli-green'
                    }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* --- RIGHT SLOT (Desktop CTA / Mobile CTA + Hamburger) --- */}
            <div className="flex justify-end items-center shrink-0">
              {/* Desktop CTA */}
              <div className="hidden md:block">
                <Link
                  href={whatsappHref}
                  target="_blank"
                  className="bg-forbli-green text-white px-5 py-1.5 w-fit mx-auto rounded-full font-semibold hover:bg-[#ccda43] transition-colors flex items-center gap-2.5 group text-xs lg:text-sm"
                >
                  <span>Garanta sua unidade!</span>
                  <div className="w-8 h-8 bg-[#ccf03e] rounded-full flex items-center justify-center transition-colors">
                    <svg
                      data-bbox="20 20 160 160"
                      viewBox="0 0 200 200"
                      width="48"
                      height="48"
                      xmlns="http://www.w3.org/2000/svg"
                      data-type="color"
                      className="w-8 h-8 group-hover:rotate-[300deg] transition-transform duration-300"
                    >
                      <g>
                        <path fill="#CCF03E" d="M180 100c0 44.183-35.817 80-80 80s-80-35.817-80-80 35.817-80 80-80 80 35.817 80 80z" data-color="1"></path>
                        <path d="M131.151 102.192c.008-.018.01-.037.017-.055.096-.256.155-.53.155-.82a2.34 2.34 0 0 0-.154-.818c-.007-.02-.011-.04-.019-.06a2.337 2.337 0 0 0-.452-.708c-.011-.012-.014-.027-.025-.039l-25.349-26.59a2.354 2.354 0 0 0-3.407 3.247l21.559 22.614H71.029a2.353 2.353 0 0 0 0 4.706h52.446l-21.558 22.612a2.354 2.354 0 0 0 3.407 3.248l25.349-26.59c.008-.009.011-.021.02-.031.193-.208.35-.448.458-.716z" fill="#020202" data-color="2"></path>
                      </g>
                    </svg>
                  </div>
                </Link>
              </div>
              {/* Mobile Hamburger */}
              <div className="flex md:hidden items-center">
                <button onClick={() => setIsMenuOpen(true)} className="flex h-12 w-12 items-center justify-center text-3xl text-gray-900" aria-label="Abrir menu">
                  <img className='w-12' src={getCdnUrl("/images/common/icon-menu.webp")} alt="" />
                </button>
              </div>
            </div>

          </div>
        </div>
      </nav >

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-start justify-end pt-2 pr-3 bg-black/50"
          >
            <motion.div
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ type: "tween", ease: "easeInOut", duration: 0.4 }}
              className="w-[88%] max-w-sm rounded-3xl bg-white p-6 shadow-xl sm:w-[70%] sm:max-w-md"
            >
              <div className="flex justify-between">
                <div className="mt-1 space-y-5 w-full pr-4">
                  <div>
                    <Link
                      href="/"
                      className={`text-xl font-bold transition-colors ${pathname === '/'
                        ? "text-forbli-green border-b-2 border-forbli-green"
                        : "text-gray-900"
                        }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Início
                    </Link>
                  </div>

                  {/* Mobile Empreendimentos Accordion */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setIsMobileEmpreendimentosOpen((prev) => !prev)}
                      className={`text-xl font-bold transition-colors flex items-center justify-between w-full ${
                        isEmpreendimentosActive ? "text-forbli-green" : "text-gray-900"
                      }`}
                    >
                      <span>Empreendimentos</span>
                      <FiChevronDown className={`w-5 h-5 transition-transform duration-200 ${isMobileEmpreendimentosOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {isMobileEmpreendimentosOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25 }}
                          className="pl-4 pt-3 space-y-3 overflow-hidden"
                        >
                          <Link
                            href="/park-europa"
                            className={`block text-lg font-medium transition-colors ${
                              pathname === '/park-europa' ? 'text-forbli-green font-bold' : 'text-gray-700 hover:text-forbli-green'
                            }`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            Park Europa
                          </Link>
                          <Link
                            href="/park-alterosa"
                            className={`block text-lg font-medium transition-colors ${
                              pathname === '/park-alterosa' ? 'text-forbli-green font-bold' : 'text-gray-700 hover:text-forbli-green'
                            }`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            Park Alterosa
                          </Link>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {otherNavLinks.map(link => (
                    <div key={link.href}>
                      <Link
                        href={link.href}
                        className={`text-xl font-bold transition-colors ${pathname === link.href
                          ? "text-forbli-green border-b-2 border-forbli-green"
                          : "text-gray-900"
                          }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {link.label}
                      </Link>
                    </div>
                  ))}
                </div>
                <div>
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className="flex h-11 w-11 items-center justify-center text-3xl text-gray-900"
                    aria-label="Fechar menu"
                  >
                    <CgClose />
                  </button>
                </div>
              </div>
              <div className="flex justify-end mt-10">
                <img src={getCdnUrl("/images/common/icon-1.webp")} className="w-[70%]" />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
